<template>
  <div class="release-report-page">
    <v-container class="d-flex justify-center align-center min-height-screen">
      <v-card class="release-card" max-width="560" elevation="8" rounded="xl">

        <!-- Logo -->
        <div class="release-card__logo-wrap">
          <img src="/logo.png" alt="TalentSage" class="release-card__logo" />
        </div>

        <!-- Loading State -->
        <v-card-text v-if="state === 'loading'" class="text-center py-12">
          <v-progress-circular indeterminate color="#0e5071" size="48" />
          <p class="mt-4 text-body-1 text-medium-emphasis">Validating your link...</p>
        </v-card-text>

        <!-- Invalid / Expired -->
        <v-card-text v-else-if="state === 'invalid'" class="text-center py-10 px-8">
          <div class="state-icon state-icon--error">❌</div>
          <h2 class="release-card__title mt-4">Invalid or Expired Link</h2>
          <p class="text-body-1 text-medium-emphasis mt-2">{{ errorMessage }}</p>
        </v-card-text>

        <!-- No Eligible Participants -->
        <v-card-text v-else-if="state === 'no-eligible'" class="text-center py-10 px-8">
          <div class="state-icon state-icon--warn">⚠️</div>
          <h2 class="release-card__title mt-4">No Eligible Participants</h2>
          <p class="text-body-1 text-medium-emphasis mt-2">
            No participants are scheduled for report release today in this scope.
          </p>
          <div class="scope-box mt-6">
            <ScopeDetails :tokenData="tokenData" />
          </div>
        </v-card-text>

        <!-- Confirm Release -->
        <v-card-text v-else-if="state === 'confirm'" class="px-8 pb-8 pt-6">
          <div class="state-icon state-icon--info">📋</div>
          <h2 class="release-card__title mt-4 text-center">Release Reports</h2>
          <p class="text-body-1 text-medium-emphasis text-center mt-1">
            You are about to immediately release reports for the following scope.
          </p>

          <!-- Scope Details -->
          <div class="scope-box mt-6">
            <div class="scope-row">
              <span class="scope-label">Iteration</span>
              <span class="scope-value">{{ tokenData.iteration_name }}</span>
            </div>
            <div class="scope-row" v-if="tokenData.stream_name">
              <span class="scope-label">Stream</span>
              <span class="scope-value">{{ tokenData.stream_name }}</span>
            </div>
            <div class="scope-row" v-if="tokenData.group_name">
              <span class="scope-label">Group</span>
              <span class="scope-value">{{ tokenData.group_name }}</span>
            </div>
            <div class="scope-row scope-row--highlight">
              <span class="scope-label">Eligible Participants</span>
              <span class="scope-value scope-value--count">{{ tokenData.eligible_count }}</span>
            </div>
            <div class="scope-row">
              <span class="scope-label">Link Expires</span>
              <span class="scope-value">{{ formattedExpiry }}</span>
            </div>
            <div class="scope-row" v-if="tokenData.used_at">
              <span class="scope-label">Last Used</span>
              <span class="scope-value">{{ formattedLastUsed }}</span>
            </div>
          </div>

          <div class="release-warning mt-6">
            ⚠️ This will immediately make reports available to all eligible participants listed above.
          </div>

          <div class="text-center mt-6">
            <v-btn
              :style="{ backgroundColor: '#0e5071', color: 'white' }"
              size="large"
              rounded="lg"
              :loading="isReleasing"
              :disabled="isReleasing"
              @click="confirmRelease"
            >
              ✅ Confirm Release
            </v-btn>
          </div>
        </v-card-text>

        <!-- Success -->
        <v-card-text v-else-if="state === 'success'" class="text-center py-10 px-8">
          <div class="state-icon state-icon--success">✅</div>
          <h2 class="release-card__title mt-4">Reports Released!</h2>
          <p class="text-body-1 text-medium-emphasis mt-2">
            <strong>{{ releaseResult.totalReleased }}</strong> report(s) released successfully
            out of <strong>{{ releaseResult.totalFound }}</strong> eligible.
          </p>
          <div class="scope-box mt-6">
            <div class="scope-row">
              <span class="scope-label">Iteration</span>
              <span class="scope-value">{{ tokenData.iteration_name }}</span>
            </div>
            <div class="scope-row" v-if="tokenData.stream_name">
              <span class="scope-label">Stream</span>
              <span class="scope-value">{{ tokenData.stream_name }}</span>
            </div>
            <div class="scope-row" v-if="tokenData.group_name">
              <span class="scope-label">Group</span>
              <span class="scope-value">{{ tokenData.group_name }}</span>
            </div>
          </div>
        </v-card-text>

        <!-- Error during release -->
        <v-card-text v-else-if="state === 'release-error'" class="text-center py-10 px-8">
          <div class="state-icon state-icon--error">❌</div>
          <h2 class="release-card__title mt-4">Release Failed</h2>
          <p class="text-body-1 text-medium-emphasis mt-2">{{ errorMessage }}</p>
          <div class="text-center mt-6">
            <v-btn variant="outlined" @click="state = 'confirm'">Try Again</v-btn>
          </div>
        </v-card-text>

      </v-card>
    </v-container>
  </div>
</template>

<script>
import api from '../api/api.js';

export default {
  name: 'ReleaseReportPage',

  data: () => ({
    // 'loading' | 'invalid' | 'no-eligible' | 'confirm' | 'success' | 'release-error'
    state: 'loading',
    tokenData: {},
    releaseResult: {},
    errorMessage: '',
    isReleasing: false,
    token: '',
  }),

  computed: {
    formattedExpiry() {
      if (!this.tokenData.expires_at) return '';
      return new Date(this.tokenData.expires_at).toLocaleDateString('en-GB', {
        day: 'numeric', month: 'short', year: 'numeric',
      });
    },
    formattedLastUsed() {
      if (!this.tokenData.used_at) return 'Never';
      return new Date(this.tokenData.used_at).toLocaleString('en-GB', {
        day: 'numeric', month: 'short', year: 'numeric',
        hour: '2-digit', minute: '2-digit',
      });
    },
  },

  async mounted() {
    this.token = this.$route.query.token;

    if (!this.token) {
      this.errorMessage = 'No token found in this link. Please check the URL.';
      this.state = 'invalid';
      return;
    }

    await this.validateToken();
  },

  methods: {
    async validateToken() {
      this.state = 'loading';
      try {
        const response = await api.post('/validate-release-token', {
          token: this.token,
        });

        this.tokenData = response.data;

        if (this.tokenData.eligible_count === 0) {
          this.state = 'no-eligible';
        } else {
          this.state = 'confirm';
        }

      } catch (error) {
        const status = error.response?.status;
        if (status === 404) {
          this.errorMessage = 'This link is invalid. Please request a new one.';
        } else if (status === 410) {
          this.errorMessage = 'This release link has expired (links are valid for 7 days).';
        } else {
          this.errorMessage = error.response?.data?.message || 'Something went wrong. Please try again.';
        }
        this.state = 'invalid';
      }
    },

    async confirmRelease() {
      this.isReleasing = true;
      try {
        const response = await api.post('/release-reports-by-iteration', {
          iteration_id: this.tokenData.iteration_id,
          stream_id: this.tokenData.stream_id || null,
          group_id: this.tokenData.group_id || null,
          token: this.token, // passed so backend can record used_at + used_by_ip
        });

        this.releaseResult = response.data;
        this.state = 'success';

      } catch (error) {
        this.errorMessage = error.response?.data?.message || 'Release failed. Please try again.';
        this.state = 'release-error';
      } finally {
        this.isReleasing = false;
      }
    },
  },
};
</script>

<style scoped>
.release-report-page {
  min-height: 100vh;
  background: #f4f6f9;
}

.min-height-screen {
  min-height: 100vh;
}

.release-card {
  width: 100%;
  background-color: rgba(255, 255, 255, 0.97) !important;
}

.release-card__logo-wrap {
  display: flex;
  justify-content: center;
  padding: 28px 0 0 0;
}

.release-card__logo {
  height: 48px;
  object-fit: contain;
}

.release-card__title {
  font-size: 22px;
  font-weight: 500;
  color: #0e5071;
}

/* State icons */
.state-icon {
  font-size: 48px;
  text-align: center;
}

/* Scope detail box */
.scope-box {
  background: #f7fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  overflow: hidden;
}

.scope-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 16px;
  border-bottom: 1px solid #eef2f7;
  font-size: 14px;
}

.scope-row:last-child {
  border-bottom: none;
}

.scope-row--highlight {
  background: #eaf4fb;
}

.scope-label {
  color: #666;
  font-weight: 500;
  min-width: 140px;
}

.scope-value {
  color: #222;
  text-align: right;
}

.scope-value--count {
  font-size: 20px;
  font-weight: 700;
  color: #0e5071;
}

/* Warning box */
.release-warning {
  background: #fff3cd;
  border: 1px solid #ffc107;
  border-radius: 6px;
  padding: 12px 16px;
  font-size: 13px;
  color: #856404;
}

/* Responsive */
@media (max-width: 600px) {
  .release-card {
    margin: 16px;
  }

  .scope-label {
    min-width: 110px;
  }
}
</style>